import { FolderOpen, Inbox, Paperclip, Trash2, Upload } from "lucide-react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { UploadForm, UploadMode } from "~/utils/upload-form";
import { Button, Card, Segmented, Switch, Text, Dropzone, message } from "~/components/ui";

type UploadDropzoneCardProps = {
  control: Control<UploadForm>;
  withVision: boolean;
  fileList: File[];
  uploadMode: UploadMode;
  onUploadModeChange: (mode: UploadMode) => void;
  onFileListChange: (fileList: File[]) => void;
  isSubmitting: boolean;
  onSubmit: () => void;
};

/** Folder picks keep their path in `webkitRelativePath`; folder drops already carry it in `name`. */
const displayFileName = (file: File) => file.webkitRelativePath || file.name;

export function UploadDropzoneCard({
  control,
  withVision,
  fileList,
  uploadMode,
  onUploadModeChange,
  onFileListChange,
  isSubmitting,
  onSubmit,
}: UploadDropzoneCardProps) {
  const { t } = useTranslation("admin");

  return (
    <Card size="small" className="bg-noa-hover">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="mb-3">
            <Segmented
              data-testid="upload-mode-toggle"
              value={uploadMode}
              onChange={(value) => onUploadModeChange(value as UploadMode)}
              options={[
                { value: "files", label: t("uploadAdmin.mode.files"), icon: <Upload className="size-3.5" /> },
                { value: "folder", label: t("uploadAdmin.mode.folder"), icon: <FolderOpen className="size-3.5" /> },
              ]}
            />
          </div>
          <Dropzone
            onFiles={(files) => onFileListChange([...fileList, ...files])}
            multiple
            directory={uploadMode === "folder"}
            accept={uploadMode === "folder" ? undefined : ".pdf,.txt,.docx,.doc,.xlsx,.csv,.msg"}
            onReject={(files) =>
              message.warning(
                t("uploadAdmin.dropzone.rejected", {
                  count: files.length,
                  names: files.map(displayFileName).join(", "),
                })
              )
            }
            className="bg-noa py-6"
          >
            <span className="mb-2 text-noa-primary">
              {uploadMode === "folder" ? <FolderOpen className="size-12" /> : <Inbox className="size-12" />}
            </span>
            <p className="m-0 text-base text-noa">
              {uploadMode === "folder" ? t("uploadAdmin.dropzone.folderText") : t("uploadAdmin.dropzone.text")}
            </p>
            <p className="m-0 text-sm text-noa-muted">
              {uploadMode === "folder" ? t("uploadAdmin.dropzone.folderHint") : t("uploadAdmin.dropzone.hint")}
            </p>
          </Dropzone>
          {fileList.length > 0 && (
            <ul className="m-0 mt-2 flex max-h-64 list-none flex-col overflow-y-auto p-0 text-sm">
              {fileList.map((file, index) => (
                <li
                  key={`${index}-${displayFileName(file)}`}
                  className="group flex items-center gap-2 rounded px-1 py-0.5 text-noa hover:bg-noa-hover"
                >
                  <Paperclip className="size-3.5 shrink-0 text-noa-muted" />
                  <span className="min-w-0 flex-1 truncate">{displayFileName(file)}</span>
                  <button
                    type="button"
                    aria-label={t("common:delete")}
                    onClick={() => onFileListChange(fileList.filter((_, i) => i !== index))}
                    className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-noa-muted opacity-0 group-hover:opacity-100 hover:text-noa-danger focus-visible:opacity-100"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col items-end gap-3 lg:min-w-48">
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <Text className="text-xs">{t("uploadAdmin.form.withVision")}</Text>
              <Controller
                name="withVision"
                control={control}
                render={({ field }) => (
                  <Switch
                    data-testid="vision-toggle"
                    size="small"
                    checked={field.value}
                    onChange={field.onChange}
                    aria-label={t("uploadAdmin.form.withVision")}
                  />
                )}
              />
            </div>
            {!withVision && (
              <Text className="text-xs text-orange-600 text-right max-w-48">
                ⚠️ {t("uploadAdmin.sync.maximo.visionWarning")}
              </Text>
            )}
          </div>
          <Button
            data-testid="start-upload-button"
            variant="primary"
            size="large"
            icon={<Upload className="size-4" />}
            loading={isSubmitting}
            disabled={fileList.length === 0}
            onClick={onSubmit}
          >
            {t("uploadAdmin.actions.upload")} ({fileList.length})
          </Button>
        </div>
      </div>
    </Card>
  );
}
